/**
 * A serial write queue.
 *
 * Every mutation of the journal runs through here, one at a time. Two reasons, and the second is
 * the one that bites:
 *
 * 1. **Order.** Journal entries must land in the order the user made the edits. Concurrent
 *    IndexedDB transactions do not promise that.
 * 2. **Sequence assignment.** Reading the last sequence, adding one, and writing the record is a
 *    read-modify-write across an `await`. Two appends interleaving there would both claim the
 *    same number, and one of the two edits would be lost the next time the journal was replayed.
 *
 * A mutex would do the same job; a chained promise is the same thing with less to go wrong.
 */
export function createWriteQueue() {
    let tail = Promise.resolve();
    let size = 0;
    return {
        get size() {
            return size;
        },
        run(task) {
            size += 1;
            const run = async () => {
                try {
                    return await task();
                }
                finally {
                    // Decremented before the result settles, so a caller reading `size` in a `then` sees
                    // the queue as it will be, not as it was.
                    size -= 1;
                }
            };
            const result = tail.then(run);
            // The chain must survive a failed task: one rejected write should not stall every write
            // after it. The caller still receives the rejection through `result`.
            tail = result.then(() => undefined, () => undefined);
            return result;
        },
        idle() {
            return tail.then(() => undefined);
        },
    };
}
//# sourceMappingURL=queue.js.map